import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  Icon,
  Image,
  RadioGroup,
  theme,
} from '@energiebespaarders/symbols';
import { Medium } from '@energiebespaarders/symbols/helpers';
import { ArrowLeft } from '@energiebespaarders/symbols/icons/line';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useKeyPressEvent, useLocalStorage } from 'react-use';
import { getInlineSolution } from '~/envCustomer/MySingleQuote/utils';
import { expressSolutions, fastLaneSlugs } from '~/utils/expressRequests';
import ActionButton from '../ActionButton';
import { selfServiceIntakeSolutionsNL } from '../Estimates/constants';
import { estimateProductPhotos } from '../Estimates/estimateProducts';
import OmniformCard from '../Omniform/OmniformCard';
import { QKeyGeneral } from './types';
import type { Solution } from '@energiebespaarders/constants';

interface ExitCardProps {
  onNext: () => void;
  onPrevious: () => void;
  onSetSelectedInterests: (interests: Solution[]) => void;
}

// TODO: Make the ProductInterestInput Omniform-independent, so we can use it here
// TODO: Show a redirect message if the user selects a single interest that has a fast lane slug

const IntakeInterestsCard: React.FC<ExitCardProps> = ({
  onNext,
  onPrevious,
  onSetSelectedInterests,
}) => {
  const router = useRouter();

  const [initialInterests] = useLocalStorage<Solution[]>('selected-interests', []);
  const [selectedInterests, setSelectedInterests] = useState<Solution[]>(initialInterests || []);
  const [hasSingleInterest, setHasSingleInterest] = useState(false);
  const singleInterest = selectedInterests?.[0];

  const handleSubmit = () => {
    if (!selectedInterests || selectedInterests?.length === 0) return;
    if (selectedInterests?.length === 1) {
      return setHasSingleInterest(true);
    }
    return onNext();
  };

  useKeyPressEvent('ArrowLeft', onPrevious);

  const toggleInterest = (interest: Solution) => {
    const newInterests = selectedInterests?.includes(interest)
      ? selectedInterests.filter(i => i !== interest)
      : [...(selectedInterests || []), interest];
    setSelectedInterests(newInterests);
    onSetSelectedInterests(newInterests);
  };

  return (
    <OmniformCard p={4} animation="none" id="omniform-interests-card">
      {hasSingleInterest && singleInterest ? (
        <Box width={1} textAlign="center" mb={3} pt={[2, 3, 5]}>
          {estimateProductPhotos[singleInterest] && (
            <Image
              src={estimateProductPhotos[singleInterest]}
              alt={getInlineSolution(singleInterest)}
              masked
              maskHeight="120px"
            />
          )}
          <p style={{ fontSize: theme.type.scale[4], letterSpacing: '-0.025em' }}>
            <Medium>
              Wil je direct offertes aanvragen voor{' '}
              {getInlineSolution(singleInterest).toLowerCase()}?
            </Medium>
          </p>
          <p>Je slaat dan de rest van de Woningscan over.</p>
          <Box width={1} pt={5}>
            <Button
              bgColor="grayDarker"
              minimal
              onClick={onNext}
              style={{ borderRadius: 3, borderColor: theme.colors.grayDark }}
              py={2}
              px={5}
              mb={0}
            >
              Door met de Woningscan
            </Button>
            <ActionButton
              id="omniform-interests-card-skip-button"
              onClick={() =>
                router.push(
                  `/offertes-aanvragen/${fastLaneSlugs[selectedInterests[0]]}?from=intake`,
                )
              }
            >
              Direct offertes aanvragen →
            </ActionButton>
          </Box>
        </Box>
      ) : (
        <form onSubmit={handleSubmit}>
          <Flex flexWrap="wrap">
            <Box width={1}>
              <Icon icon={ArrowLeft} stroke="gray" onClick={onPrevious} />
            </Box>

            <Box width={1} pt={[2, 3, 5]}>
              <Container size="md" textAlign="center">
                <p style={{ fontSize: theme.type.scale[4], letterSpacing: '-0.025em' }}>
                  <Medium>In welke producten ben je geïnteresseerd?</Medium>
                </p>
                <p>Je kunt meerdere antwoorden selecteren</p>
              </Container>
            </Box>

            <Box width={1} pt={[2, 2, 5]} px={[0, 2, 5, 10]}>
              <RadioGroup
                name="interests"
                onChange={toggleInterest}
                className="custom-focus-checkbox"
              >
                {expressSolutions.map(solution => (
                  <Checkbox
                    key={`option-${solution}`}
                    value={solution}
                    bgColor="greenDark"
                    label={selfServiceIntakeSolutionsNL[solution]}
                    checked={selectedInterests?.includes(solution)}
                    style={{ height: 'auto' }}
                  />
                ))}
              </RadioGroup>

              <Box width={1} textAlign="center" py={[5, 5, 10]}>
                <Button
                  bgColor="yellow"
                  color="grayBlack"
                  type="submit"
                  disabled={selectedInterests?.length === 0}
                  style={{ borderRadius: 3 }}
                  px={10}
                  py={2}
                  mr={0}
                  mb={0}
                  id={`next-button-${QKeyGeneral.CustomerSolutionInterest}`}
                  data-testid="omniform-button"
                >
                  Volgende →
                </Button>
              </Box>
            </Box>
          </Flex>
        </form>
      )}
    </OmniformCard>
  );
};

export default IntakeInterestsCard;
